import type { WidgetData } from "@/domains/buildings/types/buildingWidget";

/** Per-group completeness, used by the summary card's category breakdown. */
export interface CategoryCompleteness {
  id: string;
  title: string;
  confirmed: number;
  pending: number;
  total: number;
}

/** A conflicted field, surfaced as an actionable row on the dashboard summary. */
export interface ConflictField {
  /** WidgetField.id (attribute_nm) — used to focus the field in the sidebar. */
  id: string;
  /** Display label, e.g. "Net area". */
  label: string;
  /** Id of the group containing the field (for opening it in the sidebar). */
  groupId: string;
}

/** Derived, render-ready completeness figures for the Building Data summary card. */
export interface Completeness {
  /** Confirmed / total, rounded to a whole percent. 0 when total is 0. */
  pct: number;
  confirmed: number;
  pending: number;
  empty: number;
  total: number;
  /** Fields whose sources disagree (WidgetField.hasConflict), across all groups. */
  conflicts: number;
  /** The conflicted fields themselves, in group order — length === conflicts. */
  conflictFields: ConflictField[];
  categories: CategoryCompleteness[];
}

/**
 * Pure derivation of the summary card's numbers from the widget payload.
 *
 * "Complete" is confirmed-only (a human has verified the value); pending
 * machine-proposed values are reported separately and never inflate `pct`.
 * Safe on `undefined` (loading) and on an empty building (`totalCount === 0`).
 */
export function deriveCompleteness(data: WidgetData | undefined): Completeness {
  const confirmed = data?.confirmedCount ?? 0;
  const pending = data?.pendingCount ?? 0;
  const empty = data?.emptyCount ?? 0;
  const total = data?.totalCount ?? 0;

  const pct = total > 0 ? Math.round((confirmed / total) * 100) : 0;

  const groups = data?.groups ?? [];
  const conflictFields: ConflictField[] = groups.flatMap((g) =>
    g.fields.filter((f) => f.hasConflict).map((f) => ({ id: f.id, label: f.label, groupId: g.id }))
  );
  const conflicts = conflictFields.length;
  const categories: CategoryCompleteness[] = groups.map((g) => ({
    id: g.id,
    title: g.title,
    confirmed: g.fields.filter((f) => f.state === "confirmed").length,
    pending: g.fields.filter((f) => f.state === "pending").length,
    total: g.fields.length,
  }));

  return { pct, confirmed, pending, empty, total, conflicts, conflictFields, categories };
}
